import React, { useMemo } from "react";
import { css } from "@linaria/core";
import { colors, spacings } from "@ui/Theme";
import type { Quantity } from "../../../../../api/useQuantities";
import classNames from "classnames";
import formatNumber from "@ui/i18n/formatNumber";
import Text from "@ui/Text";
import t from "../../../../../i18n";

const isAroundZero = (mean: number) => {
  const absoluteValue = Math.abs(mean);
  return absoluteValue > 0 && absoluteValue < 0.1;
};

const Evolution = ({
  performanceIndicator,
  mean,
  previousMean,
}: {
  performanceIndicator: Quantity;
  mean: number;
  previousMean: number;
}) => {
  const isClosedZero = isAroundZero(previousMean);
  const sabilityEvolution = isAroundZero(mean - previousMean);
  const improvementEvolution = !sabilityEvolution && previousMean < mean;
  const degradationEvolution = !sabilityEvolution && previousMean > mean;
  const evolutionState = sabilityEvolution
    ? "stable"
    : improvementEvolution
      ? "improvement"
      : "deterioration";

  const color = useMemo(() => {
    if (improvementEvolution) return colors.chlorine700;
    if (degradationEvolution) return colors.oxygen500;
    return colors.carbon800;
  }, [improvementEvolution, degradationEvolution]);

  return (
    <div className={averageLossesEvolution}>
      <div
        className={classNames(marker)}
        style={{
          backgroundColor: color,
        }}
      />
      <Text type="body2SemiBold" style={{ color }}>
        {t("project.widget.average_losses.evolution.state", evolutionState)}
      </Text>
      <Text type="body2">
        {t(
          "project.widget.average_losses.evolution.value",
          `${isClosedZero ? "≈ " : ""}${formatNumber(previousMean, {
            precision: previousMean === 0 ? 0 : 1,
            unit: performanceIndicator?.unit ?? "-",
          })}`,
        )}
      </Text>
    </div>
  );
};

export default Evolution;

//#region CSS
const averageLossesEvolution = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xxs};
`;
const marker = css`
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
`;
//#endregion CSS
